import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ModalRoot } from '@/components/ui/ModalRoot';
import {
  X, Volume2, Palette, Clock, Info, VolumeX, Play, Keyboard, BarChart3,
  Coins, Building2, Truck, Wrench, Trophy, TrendingUp,
} from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { playTestBeep } from '@/lib/audio';
import { formatEuro } from '@/lib/format';
import { Z_MODALS } from '@/lib/zIndex';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type Tab = 'audio' | 'display' | 'time' | 'stats' | 'about';

export function SettingsModal({ open, onClose }: SettingsModalProps) {
  const [tab, setTab] = useState<Tab>('audio');
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const dur = reduceMotion ? 0.08 : 0.18;

  return (
    <AnimatePresence>
      {open && (
        <ModalRoot
          onClose={onClose}
          zIndex={Z_MODALS}
          durationSec={dur}
          contentClass="w-full max-w-2xl rounded-lg border border-border-strong bg-bg-secondary"
        >
            <header className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-accent-dispatch">
                  Einstellungen
                </p>
                <h3 id="settings-title" className="font-display text-lg font-semibold">
                  Spiel-Konfiguration
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Schließen"
                className="text-fg-muted transition hover:text-fg-primary"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr]">
              {/* Tab nav */}
              <nav className="border-b border-border-subtle px-3 py-3 md:border-b-0 md:border-r md:py-4">
                <ul className="flex flex-wrap gap-1 md:flex-col">
                  <SettingsNavItem
                    id="audio"
                    icon={Volume2}
                    label="Audio"
                    active={tab === 'audio'}
                    onClick={() => setTab('audio')}
                  />
                  <SettingsNavItem
                    id="display"
                    icon={Palette}
                    label="Anzeige"
                    active={tab === 'display'}
                    onClick={() => setTab('display')}
                  />
                  <SettingsNavItem
                    id="time"
                    icon={Clock}
                    label="Spielzeit"
                    active={tab === 'time'}
                    onClick={() => setTab('time')}
                  />
                  <SettingsNavItem
                    id="stats"
                    icon={BarChart3}
                    label="Statistik"
                    active={tab === 'stats'}
                    onClick={() => setTab('stats')}
                  />
                  <SettingsNavItem
                    id="about"
                    icon={Info}
                    label="Über"
                    active={tab === 'about'}
                    onClick={() => setTab('about')}
                  />
                </ul>
              </nav>

              <section className="px-6 py-5">
                {tab === 'audio' && <AudioTab />}
                {tab === 'display' && <DisplayTab />}
                {tab === 'time' && <TimeTab />}
                {tab === 'stats' && <StatsTab />}
                {tab === 'about' && <AboutTab />}
              </section>
            </div>
        </ModalRoot>
      )}
    </AnimatePresence>
  );
}

function SettingsNavItem({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  id: Tab;
  icon: typeof Volume2;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={[
          'flex min-h-[40px] w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition',
          active
            ? 'bg-accent-dispatch/15 text-accent-dispatch'
            : 'text-fg-muted hover:bg-bg-elevated hover:text-fg-primary',
        ].join(' ')}
      >
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </button>
    </li>
  );
}

function AudioTab() {
  const muted = useGameStore((s) => s.uiPrefs.muted);
  const volume = useGameStore((s) => s.uiPrefs.audioVolume);
  const toggleMute = useGameStore((s) => s.toggleMute);
  const setAudioVolume = useGameStore((s) => s.setAudioVolume);

  return (
    <div className="space-y-5">
      <Row label="Master-Lautstärke" hint={`${Math.round(volume * 100)} %`}>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(volume * 100)}
          onChange={(e) => setAudioVolume(Number(e.target.value) / 100)}
          className="w-full accent-accent-dispatch"
          aria-label="Master-Lautstärke"
        />
      </Row>

      <Row label="Stumm" hint={muted ? 'Sound aus' : 'Sound an'}>
        <button
          type="button"
          onClick={toggleMute}
          className="inline-flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated px-3 py-2 text-sm font-semibold transition hover:border-border-strong"
        >
          {muted ? (
            <>
              <VolumeX className="h-4 w-4" aria-hidden />
              Aktivieren
            </>
          ) : (
            <>
              <Volume2 className="h-4 w-4" aria-hidden />
              Stumm schalten
            </>
          )}
        </button>
      </Row>

      <Row label="Test" hint="Beep mit aktueller Lautstärke abspielen">
        <button
          type="button"
          onClick={() => playTestBeep({ masterVolume: volume })}
          disabled={muted}
          className={[
            'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition',
            muted
              ? 'cursor-not-allowed bg-bg-elevated text-fg-muted'
              : 'bg-accent-dispatch text-white hover:bg-accent-dispatch-hover',
          ].join(' ')}
        >
          <Play className="h-4 w-4" aria-hidden />
          Sound testen
        </button>
      </Row>
    </div>
  );
}

function DisplayTab() {
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const setReduceMotion = useGameStore((s) => s.setReduceMotion);

  return (
    <div className="space-y-5">
      <Row label="Theme" hint="Hell-Modus kommt später">
        <span className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-2 text-sm font-semibold text-fg-muted">
          Dunkel (fest)
        </span>
      </Row>

      <Row label="Animationen reduzieren" hint="Respektiert auch das System-Setting">
        <Toggle
          on={reduceMotion}
          onClick={() => setReduceMotion(!reduceMotion)}
          ariaLabel="Animationen reduzieren"
        />
      </Row>
    </div>
  );
}

function TimeTab() {
  const timeFormat = useGameStore((s) => s.uiPrefs.timeFormat);
  const setTimeFormat = useGameStore((s) => s.setTimeFormat);
  const speedMultiplier = useGameStore((s) => s.speedMultiplier);

  return (
    <div className="space-y-5">
      <Row label="Aktueller Speed" hint="Änderbar über die Speed-Pille">
        <span className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-2 text-sm font-semibold tabular-nums">
          {speedMultiplier}×
        </span>
      </Row>

      <Row label="Zeitformat">
        <div className="flex gap-1 rounded-full border border-border-subtle bg-bg-elevated p-1">
          {(['24h', '12h'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setTimeFormat(f)}
              className={[
                'min-h-[36px] rounded-full px-4 text-xs font-semibold transition',
                timeFormat === f
                  ? 'bg-accent-dispatch text-white shadow'
                  : 'text-fg-muted hover:text-fg-primary',
              ].join(' ')}
              aria-pressed={timeFormat === f}
            >
              {f}
            </button>
          ))}
        </div>
      </Row>
    </div>
  );
}

function StatsTab() {
  const stats = useGameStore((s) => s.stats);
  const successRate =
    stats.missionsCompleted + stats.missionsFailed === 0
      ? null
      : stats.missionsCompleted / (stats.missionsCompleted + stats.missionsFailed);

  // Most-frequent mission code
  const mostCommon = (() => {
    let best: [string, number] | null = null;
    for (const [code, count] of Object.entries(stats.missionsByCode)) {
      if (!best || count > best[1]) best = [code, count];
    }
    return best;
  })();

  const playtimeHours = stats.playtimeSec / 3600;
  const totalSpent =
    stats.spentOnStations +
    stats.spentOnVehicles +
    stats.spentOnRepairs +
    stats.spentOnUpkeep +
    stats.spentOnNpcAcquisition +
    stats.spentOnModules;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <StatCard
          icon={Coins}
          label="Einnahmen"
          value={formatEuro(stats.totalIncome)}
          tone="up"
        />
        <StatCard
          icon={TrendingUp}
          label="Ausgaben"
          value={formatEuro(totalSpent)}
          tone="down"
        />
      </div>

      <div className="rounded-md border border-border-subtle bg-bg-elevated/30 p-4">
        <p className="mb-3 text-[10px] uppercase tracking-wider text-fg-muted">
          Ausgaben-Aufschlüsselung
        </p>
        <ul className="space-y-1.5 text-xs">
          <SpendRow icon={Building2} label="Wachen" value={stats.spentOnStations} />
          <SpendRow icon={Truck} label="Fahrzeuge" value={stats.spentOnVehicles} />
          <SpendRow icon={Wrench} label="Reparaturen" value={stats.spentOnRepairs} />
          <SpendRow icon={Building2} label="Übernommene NPC-Wachen" value={stats.spentOnNpcAcquisition} />
          <SpendRow icon={Truck} label="Module" value={stats.spentOnModules} />
        </ul>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard
          icon={Trophy}
          label="Erfolgsrate"
          value={
            successRate === null
              ? '—'
              : `${Math.round(successRate * 100)} %`
          }
        />
        <StatCard
          icon={Clock}
          label="Spielzeit"
          value={
            playtimeHours < 1
              ? `${Math.round(stats.playtimeSec / 60)} min`
              : `${playtimeHours.toFixed(1)} h`
          }
        />
        <StatCard
          icon={Trophy}
          label="Erfolge"
          value={String(stats.missionsCompleted)}
        />
        <StatCard
          icon={Trophy}
          label="Häufigster Einsatz"
          value={mostCommon ? `${mostCommon[0]} (${mostCommon[1]}×)` : '—'}
        />
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
  tone?: 'up' | 'down';
}) {
  const toneClass =
    tone === 'up' ? 'text-status-available' : tone === 'down' ? 'text-accent-emergency' : 'text-fg-primary';
  return (
    <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3">
      <div className="flex items-center gap-1.5 text-fg-muted">
        <Icon className="h-3 w-3" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={['mt-0.5 font-display text-base font-bold tabular-nums', toneClass].join(' ')}>
        {value}
      </p>
    </div>
  );
}

function SpendRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Coins;
  label: string;
  value: number;
}) {
  return (
    <li className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-fg-secondary">
        <Icon className="h-3 w-3 text-fg-muted" aria-hidden />
        {label}
      </span>
      <span className="font-semibold tabular-nums text-fg-primary">
        -{formatEuro(value)}
      </span>
    </li>
  );
}

function AboutTab() {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-display text-lg font-bold">
            Dispatch<span className="text-accent-emergency">24</span>
            {__IS_BETA__ && (
              <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-amber-300">
                Closed Beta
              </span>
            )}
          </p>
        </div>
        <p className="mt-1 font-mono text-xs text-fg-muted">
          Version {__APP_VERSION__} ({__GIT_HASH__})
        </p>
        <p className="font-mono text-xs text-fg-muted">Build-Datum: {__BUILD_DATE__}</p>
        <p className="mt-3 text-sm text-fg-secondary">
          Browser-basierter Leitstellen-Simulator. Keine Anmeldung, kein Backend, alles im
          Browser. Spielstand wird in deinem localStorage gespeichert.
        </p>
        <div className="mt-3 flex gap-3 text-xs">
          <a href="/legal/imprint" className="text-accent-dispatch hover:underline">
            Impressum
          </a>
          <a href="/legal/privacy" className="text-accent-dispatch hover:underline">
            Datenschutz
          </a>
        </div>
      </div>

      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
        <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-fg-muted">
          <Keyboard className="h-3 w-3" aria-hidden />
          Tastenkürzel (Desktop)
        </p>
        <ul className="space-y-1.5 text-sm">
          <Shortcut keys="D" desc="Dispatch24 OS öffnen / schließen" />
          <Shortcut keys="M" desc="Sound stumm schalten" />
          <Shortcut keys="Leertaste" desc="Pause umschalten (außer Realistisch)" />
          <Shortcut keys="Esc" desc="Aktives Panel / Bau-Modus schließen" />
        </ul>
      </div>

      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4 text-xs text-fg-muted">
        Karten: © OpenStreetMap-Mitwirkende · Tiles via CARTO · Routing via OSRM Public Demo ·
        Hydrant-Daten via Overpass API. Prototyp ohne Garantien.
      </div>
    </div>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold">{label}</p>
        {hint && <p className="text-xs text-fg-muted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({
  on,
  onClick,
  ariaLabel,
}: {
  on: boolean;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      onClick={onClick}
      className={[
        'relative h-6 w-11 rounded-full transition-colors',
        on ? 'bg-accent-dispatch' : 'bg-bg-elevated',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-5' : 'translate-x-0.5',
        ].join(' ')}
        aria-hidden
      />
    </button>
  );
}

function Shortcut({ keys, desc }: { keys: string; desc: string }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-fg-secondary">{desc}</span>
      <kbd className="rounded-md border border-border-strong bg-bg-elevated px-2 py-0.5 text-[11px] font-mono text-fg-primary">
        {keys}
      </kbd>
    </li>
  );
}
